import ky from "ky";

// Use environment variable for API URL, fallback to relative path for production
const apiUrl = import.meta.env.VITE_API_URL || '';
const prefixUrl = apiUrl ? `${apiUrl}/api/v1` : "/api/v1";

function getAuthToken(): string | null {
  return localStorage.getItem("authToken");
}

const client = ky.create({
  prefixUrl,
  credentials: "include",
  retry: {
    limit: 2,
    statusCodes: [408, 429, 500, 502, 503, 504]
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const csrfToken = localStorage.getItem("csrfToken");
        if (csrfToken) {
          request.headers.set("x-csrf-token", csrfToken);
        }
        const token = getAuthToken();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
        // Always send email as fallback auth for when JWT expires
        try {
          const stored = localStorage.getItem("authUser");
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.email) {
              request.headers.set("x-user-email", parsed.email);
            }
          }
        } catch (_) {}
      }
    ],
    afterResponse: [
      async (_request, _options, response) => {
        const csrfHeader = response.headers.get("x-csrf-token");
        if (csrfHeader) {
          localStorage.setItem("csrfToken", csrfHeader);
        }
      }
    ]
  }
});

export interface ScanPayload {
  token?: string;
  guardId?: string;
  checkpointId?: string;
  qrCode?: string;
  clientTs: string;
  lat?: number;
  lng?: number;
  accuracy?: number;
  notes?: string;
  photo?: string;
  online?: boolean;
  source?: 'mobile' | 'kiosk';
  kioskId?: string;
}

export interface ScanResponse {
  event: {
    id: string;
    ts: string;
    clientTs: string;
    distanceMeters: number | null;
    proximityValid: boolean | null;
    integrityScore: number;
    status: string;
  };
  checkpoint: {
    id: string;
    name: string;
  };
  assignment: {
    id: string;
    routeId: string;
  } | null;
  scheduleStatus: string;
  window: {
    start: string;
    end: string;
    graceEnd: string;
  } | null;
}

export interface AssignmentsResponse {
  assignments: Array<{
    id: string;
    site: { id: string; name: string };
    route: {
      id: string;
      name: string;
      stops: Array<{
        id: string;
        order: number;
        status: "pending" | "completed" | "late" | "missed";
        window: { start: string; end: string; graceEnd: string };
        checkpoint: { id: string; name: string; floor: string | null; radiusMeters: number };
        lastScan: {
          id: string;
          ts: string;
          distanceMeters: number | null;
          proximityValid: boolean | null;
          status: string | null;
        } | null;
      }>;
    };
    shiftStart: string;
    shiftEnd: string;
    status: string;
  }>;
}

export interface LiveActivityResponse {
  items: Array<{
    id: string;
    guard: { id: string | null | undefined; name: string | null | undefined };
    checkpoint: { id: string; name: string };
    site: { id: string; name: string };
    assignmentId: string | null;
    timestamp: string;
    lat: number | null;
    lng: number | null;
    scheduleStatus: string;
    distanceMeters: number | null;
  }>;
}

export async function postScan(payload: ScanPayload) {
  return client.post("scan", { json: payload }).json<ScanResponse>();
}

export async function fetchMyAssignments() {
  return client.get("assignments/me").json<AssignmentsResponse>();
}

export async function fetchLiveActivity(params: Record<string, string | number | undefined>) {
  return client.get("live-activity", { searchParams: params }).json<LiveActivityResponse>();
}

export async function fetchComplianceReport(params: Record<string, string | number | undefined>) {
  return client.get("reports/compliance", { searchParams: params }).json();
}

export async function fetchAlerts(params: Record<string, string | number | undefined> = {}) {
  return client.get("alerts", { searchParams: params }).json();
}

export async function resolveAlert(id: string) {
  return client.post(`alerts/${id}/resolve`).json();
}

export { client as apiClient };
